import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import type {
  SvpAssetAdditional,
  SvpAssetId,
} from '@vgtv/api-client/lib/svp_asset';
import { PageType } from '@vgtv/api-client/lib/page';
import type {
  SvpCategoryAdditional,
  SvpCategoryId,
} from '@vgtv/api-client/lib/svp_category';

import { useAppSelector, useAppDispatch } from '../modules/hooks';
import {
  selectFavouritesIds,
  selectIsFavouritesLoaded,
} from '../modules/favourites/selectors';
import { selectCategoriesAsArray } from '../modules/categories/selectors';
import {
  addToFavourites,
  removeFromFavourites,
} from '../modules/favourites/actions';
import { useViewportType } from '../components/utils/useViewportType';
import { FavouritesListHorizontal } from '../components/Favourites/FavouritesListHorizontal/FavouritesListHorizontal';
import { FavouritesListVertical } from '../components/Favourites/FavouritesListVertical/FavouritesListVertical';
import { usePageViewTracking } from '../components/utils/usePageViewTracking';
import { getPath, routeNames } from '../shared/helpers/routes';
import { config } from '../config';
import { selectAssets } from '../modules/assets';
import type { FavouriteType } from '../helpers/favourites';
import { createFavouriteId } from '../helpers/favourites';

interface Favourite {
  item: SvpCategoryAdditional | SvpAssetAdditional;
  isFilled: boolean;
  type: FavouriteType;
}

const FavouritesPage: FC = () => {
  const [favourites, setFavourites] = useState<Favourite[]>([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const favouritesIds = useAppSelector(selectFavouritesIds);
  const favouritesIdsRef = useRef(favouritesIds);

  const vendorAssets = useAppSelector((state) =>
    selectAssets(state, config.provider)
  );

  const categories = useAppSelector((state) =>
    selectCategoriesAsArray(state, config.provider)
  );

  useEffect(() => {
    favouritesIdsRef.current = favouritesIds;
  }, [favouritesIds]);

  const isFavouritesLoaded = useAppSelector(selectIsFavouritesLoaded);

  const dispatch = useAppDispatch();
  const { isMobile } = useViewportType();

  usePageViewTracking({
    pageType: PageType.CUSTOM,
    entity: { slug: getPath(routeNames.favourites) },
  });

  useEffect(() => {
    if (!isFavouritesLoaded) {
      return;
    }
    const fav = favouritesIdsRef.current
      .map((favouriteId) => {
        const [type, entityId] = favouriteId.split(':');
        const item =
          type === 'asset'
            ? Object.values(vendorAssets).find(
                (item) => item?.id === parseInt(entityId)
              )
            : categories.find((item) => item?.id === parseInt(entityId));

        return {
          item: item,
          isFilled: true,
          type,
        };
      })
      .filter((favItem): favItem is Favourite => !!favItem.item);

    setFavourites(fav);
    setIsListLoading(false);
  }, [categories, vendorAssets, isFavouritesLoaded]);

  const handleClick = useCallback(
    (id: SvpCategoryId | SvpAssetId, type: FavouriteType) => {
      setFavourites((prevFavourites) => {
        const selectedFavourite = prevFavourites.find(
          (fav) => fav.item.id === id && fav.type === type
        );
        if (selectedFavourite) {
          if (selectedFavourite.isFilled) {
            dispatch(
              removeFromFavourites(createFavouriteId(selectedFavourite.item))
            );
          } else {
            dispatch(
              addToFavourites(createFavouriteId(selectedFavourite.item))
            );
          }

          return prevFavourites.map((favourite) =>
            favourite.item.id === id && favourite.type === type
              ? { ...favourite, isFilled: !selectedFavourite.isFilled }
              : favourite
          );
        }
        return prevFavourites;
      });
    },
    [dispatch]
  );

  const FavouritesComponent = isMobile
    ? FavouritesListVertical
    : FavouritesListHorizontal;
  return (
    <FavouritesComponent
      favourites={favourites}
      handleClick={handleClick}
      isLoading={isListLoading}
    />
  );
};

export { FavouritesPage };
